/*
 * Copyright (c) 2023 Bolmate B.V.
 */
import './style/commonSettings.scss';

import React, {FocusEventHandler, MouseEventHandler} from 'react';
import User from "../../../models/userModel";
import Account from "../../../models/accountModel";
import {ChangeFunction, ChangeKeyFunction, Link, Loader} from '@dvrd/dvr-controls';
import {CompanyErrors, PersonalErrors} from "./userErrorsInterface";
import CountryField from "../../controls/country/field/countryField";
import {BolmateButton, BolmateInput} from "../../controls";
import PlatformLabelInput from "../../controls/input/platformLabelInput";
import useRight from "../../misc/useRight";
import {AccessKey} from "../../../util/interfaces";
import {SUBSCRIPTION_ROUTE} from "../../../util/constants";

interface Props {
    onChangeUser: ChangeKeyFunction;
    onChangeAccount: ChangeKeyFunction;
    onUpdateUser: MouseEventHandler;
    onUpdateAccount: MouseEventHandler;
    onClickDelete: MouseEventHandler;
    onResetPersonalErrors: (key: keyof PersonalErrors) => FocusEventHandler;
    onResetCompanyErrors: (key: keyof CompanyErrors) => FocusEventHandler;
    onChangeCountryCode: ChangeFunction<string>;
    onChangeCountryVat: ChangeKeyFunction<string>;
    onClickDeleteUser: MouseEventHandler;
    user: User;
    account: Account;
    personalErrors: PersonalErrors;
    companyErrors: CompanyErrors;
    loading: boolean;
    countryCode: string;
    hasMultipleAccounts: boolean;
    accountChanged: boolean;
}

export default function CommonSettings(props: Props) {
    const [canEditCompany, canDeleteCompany] = useRight(AccessKey.SETTING_EDIT_COMPANY,
        AccessKey.SETTINGS_DELETE_COMPANY);
    const {
        account, onChangeAccount, onUpdateAccount, hasMultipleAccounts, onChangeUser, onUpdateUser, user,
        onChangeCountryCode, onChangeCountryVat, countryCode, loading, onClickDelete, accountChanged,
        companyErrors, onResetCompanyErrors, onResetPersonalErrors, personalErrors, onClickDeleteUser
    } = props;

    function renderPersonal() {
        return (
            <div className='block'>
                <div className='header-container'>
                    <label className='block-title'>Persoonlijke informatie</label>
                    <label className='block-sub'>Hiermee houden we contact met elkaar</label>
                </div>
                <div className='fields'>
                    <Loader active={loading}/>
                    <BolmateInput value={user.firstname} onChange={onChangeUser('firstname')} label='Voornaam *'
                                  error={personalErrors.firstname} onFocus={onResetPersonalErrors('firstname')}
                                  disabled={loading}/>
                    <BolmateInput value={user.lastname} onChange={onChangeUser('lastname')} label='Achternaam *'
                                  error={personalErrors.lastname} onFocus={onResetPersonalErrors('lastname')}
                                  disabled={loading}/>
                    <BolmateInput value={user.address} onChange={onChangeUser('address')} label='Adres *'
                                  error={personalErrors.address} onFocus={onResetPersonalErrors('address')}
                                  disabled={loading}/>
                    <BolmateInput value={user.zipCode} onChange={onChangeUser('zip_code')} label='Postcode *'
                                  error={personalErrors.zipCode} onFocus={onResetPersonalErrors('zipCode')}
                                  disabled={loading}/>
                    <BolmateInput value={user.city} onChange={onChangeUser('city')} label='Stad *'
                                  error={personalErrors.city} onFocus={onResetPersonalErrors('city')}
                                  disabled={loading}/>
                    <CountryField onChange={onChangeUser('country')} value={user.country} disabled={loading}
                                  labelAsValue/>
                    <BolmateInput value={user.email} onChange={onChangeUser('email')} error={personalErrors.email}
                                  onFocus={onResetPersonalErrors('email')} inputProps={{type: 'email'}}
                                  disabled={loading} label='Account mailadres * (om mee in te loggen)'/>
                    <BolmateInput value={user.telNumber} onChange={onChangeUser('tel_number')} label='Telefoonnummer *'
                                  error={personalErrors.phone} onFocus={onResetPersonalErrors('phone')}
                                  inputProps={{type: 'tel'}} disabled={loading}/>
                </div>
                <div className='updater'>
                    <BolmateButton onClick={onUpdateUser} label='Opslaan' disabled={loading}/>
                </div>
            </div>
        );
    }

    function renderCompany() {
        return (
            <div className='block'>
                <div className='header-container with-info'>
                    <div className='column'>
                        <label className='block-title'>Bedrijfsgegevens</label>
                        <label className='block-sub'>Deze gegevens gebruiken we richting jouw klanten</label>
                    </div>
                </div>
                <div className='fields'>
                    <Loader active={loading}/>
                    <BolmateInput value={account.companyName} onChange={onChangeAccount('company_name')}
                                  label='Bedrijfsnaam *' error={companyErrors.companyName}
                                  onFocus={onResetCompanyErrors('companyName')} disabled={loading || !canEditCompany}/>
                    {hasMultipleAccounts &&
                        <PlatformLabelInput onChange={onChangeAccount('platform_label')} value={account.platformLabel}
                                            placeholder={account.companyName} disabled={loading || !canEditCompany}/>}
                    <BolmateInput value={account.address} onChange={onChangeAccount('address')} label='Adres *'
                                  error={companyErrors.address} onFocus={onResetCompanyErrors('address')}
                                  disabled={loading || !canEditCompany}/>
                    <BolmateInput value={account.zipCode} onChange={onChangeAccount('zip_code')} label='Postcode *'
                                  error={companyErrors.zipCode} onFocus={onResetCompanyErrors('zipCode')}
                                  disabled={loading || !canEditCompany}/>
                    <BolmateInput value={account.city} onChange={onChangeAccount('city')} label='Stad *'
                                  error={companyErrors.city} onFocus={onResetCompanyErrors('city')}
                                  disabled={loading || !canEditCompany}/>
                    <CountryField onChange={onChangeAccount('country')} value={account.country}
                                  disabled={loading || !canEditCompany}
                                  labelAsValue/>
                    <BolmateInput value={account.email} onChange={onChangeAccount('email')} error={companyErrors.email}
                                  onFocus={onResetCompanyErrors('email')} inputProps={{type: 'email'}}
                                  disabled={loading || !canEditCompany}
                                  label='E-mailadres * (ontvangst van klanten mails)'/>
                    <BolmateInput value={account.telNumber} onChange={onChangeAccount('tel_number')}
                                  label='Telefoonnummer *' error={companyErrors.phone}
                                  onFocus={onResetCompanyErrors('phone')} inputProps={{type: 'tel'}}
                                  disabled={loading || !canEditCompany}/>
                </div>
                <div className='submit-row'>
                    <BolmateButton onClick={onUpdateAccount} label='Opslaan' disabled={loading || !canEditCompany}/>
                    {accountChanged &&
                        <label className='subscription-message'>Let op, jouw eigen Bolmate abonnement factuurgegevens
                            wijzigen? Dat doe je <Link route={SUBSCRIPTION_ROUTE + '?gegevens='}>hier</Link>.</label>}
                </div>
            </div>
        );
    }

    function renderAdditional() {
        return (
            <div className='block'>
                <div className='header-container with-info'>
                    <div className='column'>
                        <label className='block-title'>Aanvullende bedrijfsgegevens</label>
                        <label className='block-sub'>Deze informatie gebruiken we op de factuur voor jouw
                            klanten</label>
                    </div>
                </div>
                <div className='fields'>
                    <Loader active={loading}/>
                    <BolmateInput value={account.cocNumber} onChange={onChangeAccount('coc_number')} label='KvK nummer'
                                  disabled={loading || !canEditCompany}/>
                    <BolmateInput value={account.vatNumber} onChange={onChangeAccount('vat_number')}
                                  label='BTW nummer standaard'
                                  disabled={loading || !canEditCompany}/>
                    <BolmateInput value={account.iban} onChange={onChangeAccount('iban')} label='IBAN'
                                  disabled={loading || !canEditCompany}/>
                    {renderCountryVatNumber()}
                </div>
                <div className='submit-row'>
                    <BolmateButton onClick={onUpdateAccount} label='Opslaan' disabled={loading || !canEditCompany}/>
                    {accountChanged &&
                        <label className='subscription-message'>Let op, jouw eigen Bolmate abonnement factuurgegevens
                            wijzigen? Dat doe je <Link route={SUBSCRIPTION_ROUTE + '?gegevens='}>hier</Link>.</label>}
                </div>
            </div>
        );
    }

    function renderCountryVatNumber() {
        const value = account.vatNumbers[countryCode] ?? '';
        return (
            <div className='country-vat-number'>
                <CountryField onChange={onChangeCountryCode} value={countryCode} itemsPosition='top'
                              label='Afwijkend BTW nummer per land' disabled={!canEditCompany}/>
                <BolmateInput onChange={onChangeCountryVat(countryCode)} value={value}
                              inputProps={{placeholder: account.vatNumber}}
                              label='BTW nummer (indien van toepassing)' disabled={!canEditCompany}/>
            </div>
        );
    }

    function renderDelete() {
        return (
            <div className='btns-container'>
                {canDeleteCompany && <BolmateButton onClick={onClickDelete}
                                                    label={`Verwijder dit verkoopaccount (${account.selectablePlatformLabel})`}
                                                    disabled={loading} red secondary/>}
                <BolmateButton onClick={onClickDeleteUser} label='Verwijder geheel Bolmate account'
                               disabled={loading} red secondary/>
            </div>
        );
    }

    return (
        <div className='common-settings'>
            {renderPersonal()}
            {renderCompany()}
            {renderAdditional()}
            {renderDelete()}
        </div>
    );
}